import React, { useState, useRef } from 'react';
import useSwipe from '../../hooks/useSwipe';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Psychology as PsychologyIcon,
  Warning as WarningIcon,
  DeleteSweep as DeleteSweepIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { format, setHours, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatTime12h } from '../../utils/calendarHelpers';
import WhatsAppReminderButton from './WhatsAppReminderButton';
import { esFeriadoPeruano, obtenerNombreFeriado } from '../../utils/feriadosPeruanos';
import citaService from '../../services/citaService';
import { isMobileDevice } from '../../utils/isAndroidWebView';

const DayView = ({ 
  selectedDate, 
  events, 
  onEventClick, 
  onDateClick, 
  colorConfig,
  selectedEspecialista,
  selectedEspecialidad,
  isHoraBloqueada,
  horariosNoLaborables,
  getUserRole,
  showNotification,
  refreshEvents,
  onPrev,
  onNext    
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLowPerf = isMobileDevice();
  const navigate = useNavigate();
  
  const swipeRef = useRef(null);
  useSwipe(swipeRef, onPrev, onNext);

  const [eliminarPosterioresModalOpen, setEliminarPosterioresModalOpen] = useState(false);
  const [citaParaEliminarPosteriores, setCitaParaEliminarPosteriores] = useState(null);
  const [loadingEliminarPosteriores, setLoadingEliminarPosteriores] = useState(false);
  const [citasPosterioresInfo, setCitasPosterioresInfo] = useState(null);
  
  const canCreate = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let dateToFormat = dateString;
      if (dateString.includes('T')) {
        dateToFormat = dateString.split('T')[0];
      }
      const [year, month, day] = dateToFormat.split('-');
      const date = new Date(year, month - 1, day, 12, 0, 0);
      return format(date, 'dd/MM/yyyy', { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  const handleEliminarCita = async (citaId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta cita?')) {
      return;
    }
    
    try {
      await citaService.deleteCita(citaId);
      showNotification('Cita eliminada', 'success');
      refreshEvents();
    } catch (error) {
      showNotification('Error al eliminar la cita', 'error');
    }
  };

  const handleEliminarCitaYPosteriores = async (citaId) => {
    try {
      const conteoInfo = await citaService.contarCitasPosteriores(citaId);
      
      if (conteoInfo.total === 0) {
        showNotification('No se encontraron citas posteriores', 'warning');
        return;
      }
      
      const citaEncontrada = events.find(event => event.id === citaId);
      
      setCitaParaEliminarPosteriores(citaEncontrada || { id: citaId });
      setCitasPosterioresInfo({
        totalCitas: conteoInfo.total,
        paciente: conteoInfo.paciente,
        fechaDesde: conteoInfo.fecha_desde,
        ejemplos: conteoInfo.ejemplos || []
      });
      
      setEliminarPosterioresModalOpen(true);
    } catch (error) {
      showNotification('Error al consultar citas', 'error');
    }
  };

  const handleConfirmarEliminacionPosteriores = async () => {
    if (!citaParaEliminarPosteriores) return;
    
    setLoadingEliminarPosteriores(true);
    try {
      const result = await citaService.eliminarCitaYPosteriores(citaParaEliminarPosteriores.id);
      
      showNotification(`${result.citas_eliminadas || 0} citas eliminadas`, 'success');
      
      setEliminarPosterioresModalOpen(false);
      setCitaParaEliminarPosteriores(null);
      setCitasPosterioresInfo(null);
      refreshEvents();
    } catch (error) {
      showNotification('Error al eliminar citas', 'error');
    } finally {
      setLoadingEliminarPosteriores(false);
    }
  };

  const handleDateClickEnhanced = (date) => {
    if (getDay(date) === 0) {
      showNotification('No se pueden crear citas los domingos', 'warning');
      return;
    }
    
    if (canCreate()) {
      let url = `/citas/nueva?fecha=${format(date, 'yyyy-MM-dd')}`;
      if (selectedEspecialista) url += `&especialista=${selectedEspecialista}`;
      if (selectedEspecialidad) url += `&especialidad=${selectedEspecialidad}`;
      navigate(url);
    } else {
      showNotification('No tiene permisos para crear citas', 'warning');
    }
  };
  
  const esFeriadoFecha = (fecha) => {
    const fechaStr = format(fecha, 'yyyy-MM-dd');
    return esFeriadoPeruano(fechaStr);
  };
  
  const getHourAvailability = (hourStr) => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    if (esFeriadoFecha(selectedDate)) {
      return {
        disponible: false,
        motivo: `Feriado: ${obtenerNombreFeriado(dateStr)}`,
        tipo: 'feriado'
      };
    }
    
    if (selectedEspecialista) {
      const horarioInfo = isHoraBloqueada(dateStr, hourStr, selectedEspecialista);
      
      if (horarioInfo.esAdministrativa) {
        return { disponible: false, motivo: 'Hora administrativa', tipo: 'administrativa' };
      }
      
      if (!horarioInfo.laborable) {
        return { disponible: false, motivo: horarioInfo.motivo || 'No disponible', tipo: 'no_disponible' };
      }
    }
    
    const citasEnHora = dayEvents.filter(event => {
      const esActiva = !['Reprogramo', 'Cancelada'].includes(event.estado);
      const eventTime = event.horaInicio || '00:00';
      return eventTime.startsWith(hourStr.substring(0, 5)) && esActiva;
    });
    
    if (citasEnHora.length > 0) {
      return { disponible: false, motivo: `Ocupado: ${citasEnHora[0].pacienteNombre}`, tipo: 'ocupado' };
    }
    
    return { disponible: true, motivo: 'Disponible', tipo: 'disponible' };
  };

  const dayEvents = events.filter(event => {
    const eventFecha = typeof event.fecha === 'string' 
      ? event.fecha 
      : (event.fecha instanceof Date ? format(event.fecha, 'yyyy-MM-dd') : event.fecha);
    
    return eventFecha === format(selectedDate, 'yyyy-MM-dd');
  });

  const eventsByHour = {};
  dayEvents.forEach(event => {
    const hour = event.horaInicioNum;
    if (!eventsByHour[hour]) eventsByHour[hour] = [];
    eventsByHour[hour].push(event);
  });

  // Función para obtener las horas según el especialista
  const getHorasDisponibles = () => {
    if (!selectedEspecialista || !horariosNoLaborables) {
      return Array.from({ length: 12 }, (_, i) => i + 8);
    }
    
    let horarios = horariosNoLaborables.get(parseInt(selectedEspecialista));
    
    if (typeof horarios === 'string') {
      try { horarios = JSON.parse(horarios); } 
      catch { return Array.from({ length: 12 }, (_, i) => i + 8); }
    }
    
    if (!horarios) return Array.from({ length: 12 }, (_, i) => i + 8);
    
    const horariosGenerales = horarios.horarios_generales || horarios;
    if (!horariosGenerales || typeof horariosGenerales !== 'object') {
      return Array.from({ length: 12 }, (_, i) => i + 8);
    }
    
    const dayOfWeek = selectedDate.getDay();
    const nombresDias = { 0: 'domingo', 1: 'lunes', 2: 'martes', 3: 'miercoles', 4: 'jueves', 5: 'viernes', 6: 'sabado' };
    const nombreDia = nombresDias[dayOfWeek];
    
    if (!nombreDia || nombreDia === 'domingo') {
      return Array.from({ length: 12 }, (_, i) => i + 8);
    }
    
    const horariosDia = horariosGenerales[nombreDia];
    if (!horariosDia || typeof horariosDia !== 'object') {
      return Array.from({ length: 12 }, (_, i) => i + 8);
    }
    
    const horasDia = Object.keys(horariosDia);
    if (horasDia.length === 0) return Array.from({ length: 12 }, (_, i) => i + 8);
    
    return horasDia.sort((a, b) => {
      const [aHour, aMin] = a.split(':').map(Number);
      const [bHour, bMin] = b.split(':').map(Number);
      return (aHour * 60 + aMin) - (bHour * 60 + bMin);
    });
  };
  
  const hours = getHorasDisponibles();
  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  const separateEventsByStatus = (hourEvents) => {
    const activos = hourEvents.filter(event => !['Reprogramo', 'Cancelada'].includes(event.estado));
    const historicos = hourEvents.filter(event => ['Reprogramo', 'Cancelada'].includes(event.estado));
    return { activos, historicos };
  };

  const renderEvent = (event, isHistorico = false) => (
    <Card 
      key={`${isHistorico ? 'hist' : 'active'}-${event.id}`}
      sx={{ 
        mb: 1, 
        background: isHistorico ? '#ff9800' : event.color,
        color: 'white',
        opacity: isHistorico ? 0.8 : 1,
        cursor: 'pointer',
        borderRadius: '8px',
        border: isHistorico ? '2px dashed rgba(255, 111, 0, 0.6)' : 'none'
      }}
      onClick={(e) => {
        e.stopPropagation();
        onEventClick(event);
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant="body2" fontWeight="600">
                {formatTime12h(event.horaInicio)} - {formatTime12h(event.horaFin)}
              </Typography>
              
              <Chip
                label={event.estado}
                size="small"
                sx={{ 
                  fontSize: '0.65rem',
                  height: 20,
                  background: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  fontWeight: '700'
                }}
              />
            </Box>

            <Typography variant="body2" sx={{ fontWeight: isHistorico ? 'normal' : '500', fontStyle: isHistorico ? 'italic' : 'normal' }}>
              {isHistorico && '[HISTÓRICO] '}{event.pacienteNombre}
            </Typography>
            
            {(event.especialistaNombre || event.etapaNombre) && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                  {event.especialistaNombre && `${event.especialistaNombre}`}
                  {event.especialistaNombre && event.etapaNombre && ' - '}
                  {event.etapaNombre && `${event.etapaNombre}`}
                </Typography>
              </Stack>
            )}
          </Box>
          
          {!isHistorico && (event.extendedProps?.pacienteWhatsapp || event.extendedProps?.pacienteTelefono) && (
            <Box sx={{ ml: 1 }}>
              <WhatsAppReminderButton
                citaId={event.id}
                cita={event}
                size="small"
                disabled={['Cancelada', 'Realizada', 'No asistió', 'Reprogramo'].includes(event.estado)}
              />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
  
  return (
    <Box
      ref={isMobile ? swipeRef : null}
      sx={{ width: '100%', userSelect: 'none' }}
    >
      {/* Header con navegación para móvil */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 2,
        px: 1
      }}>
        {isMobile && (
          <Button 
            onClick={onPrev} 
            size="small" 
            sx={{ minWidth: '40px' }}
          >
            <ChevronLeftIcon />
          </Button>
        )}
        
        <Typography 
          variant={isMobile ? "h6" : "h5"}
          align="center" 
          sx={{ 
            fontWeight: '700',
            color: '#1976d2',
            flex: 1
          }}
        >
          {format(selectedDate, isMobile ? "EEE d MMM" : "EEEE d 'de' MMMM 'de' yyyy", { locale: es })}
        </Typography>
        
        {isMobile && (
          <Button 
            onClick={onNext} 
            size="small" 
            sx={{ minWidth: '40px' }}
          >
            <ChevronRightIcon />
          </Button>
        )}
      </Box>

      {/* Indicador de swipe para móvil */}
      {isMobile && (
        <Typography 
          variant="caption" 
          align="center" 
          sx={{ 
            display: 'block', 
            mb: 1, 
            color: '#999',
            fontSize: '0.7rem'
          }}
        >
          ← Desliza para cambiar de día →
        </Typography>
      )}
      
      {dayEvents.length === 0 && !selectedEspecialista ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '12px', background: '#f5f5f5' }}>
          <Typography color="text.secondary">
            No hay citas programadas para este día
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ 
          p: isMobile ? 1.5 : 2,
          borderRadius: '12px',
          background: '#fff',
          border: '1px solid #e0e0e0'
        }}>
          {hours.map(hour => {
            const hourNum = typeof hour === 'string' ? parseInt(hour.split(':')[0]) : hour;
            const hourStr = typeof hour === 'string' ? hour : `${hour.toString().padStart(2, '0')}:00`;
            
            const hourEvents = eventsByHour[hourNum] || [];
            const hourAvailability = getHourAvailability(hourStr);
            const esDisponible = hourAvailability.disponible;
            const tipoEstado = hourAvailability.tipo;
            
            const { activos, historicos } = separateEventsByStatus(hourEvents);
            
            const horarioInfo = selectedEspecialista ? 
              isHoraBloqueada(dateStr, hourStr, selectedEspecialista) : 
              { bloqueada: false, laborable: true, motivo: '' };
            
            // Colores simplificados
            let backgroundColor = 'transparent';
            let borderColor = '#e0e0e0';
            let textColor = 'inherit';
            
            if (tipoEstado === 'feriado') {
              backgroundColor = '#ffebee';
              borderColor = '#ef5350';
              textColor = '#c62828';
            } else if (tipoEstado === 'administrativa') {
              backgroundColor = '#9C27B0';
              textColor = '#ffffff';
            } else if (tipoEstado === 'no_disponible') {
              backgroundColor = '#9e9e9e';
              textColor = '#ffffff';
            } else if (tipoEstado === 'ocupado') {
              backgroundColor = '#fff3e0';
              borderColor = '#ff9800';
            } else if (esDisponible) {
              backgroundColor = '#e8f5e9';
              borderColor = '#4caf50';
            }
            
            return (
              <Box key={hourStr} sx={{ mb: 0.5 }}>
                <Grid container spacing={1}>
                  <Grid item xs={2} sm={1.5}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: '700',
                        color: !horarioInfo.laborable ? '#fff' : '#666',
                        display: 'block',
                        textAlign: 'right',
                        pr: 0.5,
                        pt: 1,
                        fontSize: isMobile ? '0.65rem' : '0.75rem'
                      }}
                    >
                      {(() => {
                        const [h, m] = hourStr.split(':').map(Number);
                        const period = h >= 12 ? 'PM' : 'AM';
                        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                        return m > 0 ? `${hour12}:${m.toString().padStart(2, '0')} ${period}` : `${hour12} ${period}`;
                      })()}
                    </Typography>
                  </Grid>
                  <Grid item xs={10} sm={10.5}>
                    <Box 
                      sx={{ 
                        minHeight: 70, 
                        borderLeft: `3px solid ${borderColor}`,
                        pl: 1.5,
                        pr: 1,
                        py: 1,
                        background: backgroundColor,
                        color: textColor,
                        cursor: esDisponible ? 'pointer' : 'not-allowed',
                        border: tipoEstado === 'feriado' ? '2px dashed #ef5350' : `1px solid ${borderColor}`,
                        borderRadius: '8px'
                      }}
                      onClick={() => {
                        if (esDisponible) {
                          const clickDate = setHours(selectedDate, hourNum);
                          handleDateClickEnhanced(clickDate);
                        }
                      }}
                    >
                      {/* Estados vacíos */}
                      {!horarioInfo.laborable && (activos.length + historicos.length) === 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                          <Typography variant="caption" sx={{ color: 'inherit', fontWeight: '600' }}>
                            🚫 {horarioInfo.motivo}
                          </Typography>
                        </Box>
                      )}
                      
                      {!esDisponible && (activos.length + historicos.length) === 0 && horarioInfo.laborable && (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                          <Typography variant="caption" sx={{ color: 'inherit', fontWeight: '600' }}>
                            {tipoEstado === 'feriado' && '🎊'} 
                            {tipoEstado === 'administrativa' && '📋'} 
                            {hourAvailability.motivo}
                          </Typography>
                        </Box>
                      )}
                      
                      {esDisponible && (activos.length + historicos.length) === 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: '500' }}>
                            + Crear cita
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Eventos históricos */}
                      {historicos.map(event => renderEvent(event, true))}
                      
                      {/* Separador */}
                      {historicos.length > 0 && activos.length > 0 && (
                        <Alert severity="info" variant="outlined" sx={{ my: 1, py: 0.5, fontSize: '0.7rem' }} icon={<WarningIcon sx={{ fontSize: 14 }} />}>
                          <Typography variant="caption">
                            Horario reutilizado - {historicos.length} histórica(s) + {activos.length} activa(s)
                          </Typography>
                        </Alert>
                      )}
                      
                      {/* Eventos activos */}
                      {activos.map(event => renderEvent(event, false))}
                    </Box>
                  </Grid>
                </Grid>
                {hour < hours[hours.length - 1] && <Divider sx={{ my: 0.5, opacity: 0.3 }} />}
              </Box>
            );
          })}
        </Paper>
      )}
      
      {/* Leyenda compacta para móvil */}
      {selectedEspecialista && (
        <Paper sx={{ 
          p: 2, 
          mt: 2, 
          borderRadius: '8px',
          background: '#f5f5f5',
          border: '1px solid #e0e0e0'
        }}>
          <Typography variant="body2" sx={{ fontWeight: '700', mb: 1, color: '#333' }}>
            Leyenda:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              { color: '#e8f5e9', border: '1px solid #4caf50', label: 'Disponible' },
              { color: '#9e9e9e', label: 'No disponible' },
              { color: '#fff3e0', border: '1px solid #ff9800', label: 'Ocupado' },
              { color: '#9C27B0', label: 'Administrativa' },
              { color: '#ffebee', border: '2px dashed #ef5350', label: '🎊 Feriado' }
            ].map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  background: item.color,
                  border: item.border || 'none',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Modal de eliminación */}
      <Dialog
        open={eliminarPosterioresModalOpen}
        onClose={() => {
          setEliminarPosterioresModalOpen(false);
          setCitaParaEliminarPosteriores(null);
          setCitasPosterioresInfo(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ background: '#ffebee', color: '#d32f2f', fontWeight: '700' }}>
          Eliminar Citas
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción eliminará la cita y todas las posteriores.
          </Alert>
          
          {citasPosterioresInfo && (
            <Box sx={{ background: '#f5f5f5', borderRadius: '8px', p: 2 }}>
              <Typography variant="body2">
                <strong>Paciente:</strong> {citasPosterioresInfo.paciente}
              </Typography>
              <Typography variant="body2" color="error" sx={{ fontWeight: '700', mt: 1 }}>
                Total a eliminar: {citasPosterioresInfo.totalCitas}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setEliminarPosterioresModalOpen(false);
              setCitaParaEliminarPosteriores(null);
              setCitasPosterioresInfo(null);
            }}
            disabled={loadingEliminarPosteriores}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarEliminacionPosteriores}
            variant="contained"
            color="error"
            disabled={loadingEliminarPosteriores}
            startIcon={loadingEliminarPosteriores ? <CircularProgress size={18} /> : <DeleteSweepIcon />}
          >
            {loadingEliminarPosteriores ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DayView;
